import { useMemo } from "react";
import currency from "currency.js";
import { COLORS, TEXT_COLORS } from "../../constants/design/colors";
import { SPACING } from "../../constants/design/spacing";
import { IAevoPosition } from "../../interfaces/aevo";
import { IDeribitPosition, IDeribitAccountSummary } from "../../interfaces/deribit";
import { retrieveAssetByInstrumentName } from "../../utils/strings";

// Normalize instrument names to Aevo convention
const normalizeInstrumentName = (name: string): string => {
  // Handle PERP -> PERPETUAL
  if (name.endsWith("-PERP")) {
    return name.replace("-PERP", "-PERPETUAL");
  }

  // Handle Deribit date format (e.g., ETH-2NOV25 -> ETH-02NOV25)
  const datePattern = /^([A-Z]+)-(\d{1,2})([A-Z]{3}\d{2})-(.+)$/;
  const match = name.match(datePattern);

  if (match) {
    const [, asset, day, rest, strikeAndType] = match;
    const paddedDay = day.padStart(2, "0");
    return `${asset}-${paddedDay}${rest}-${strikeAndType}`;
  }

  return name;
};

interface IGreeksSummary {
  delta: number;
  gamma: number;
  vega: number;
  theta: number;
}

interface IPerpGreeksSummary {
  delta: number;
}

interface IAssetGreeksSummary {
  aevo: IGreeksSummary;
  deribit: IGreeksSummary;
  aevoPerp: IPerpGreeksSummary;
  deribitPerp: IPerpGreeksSummary;
  deribitBalance: IPerpGreeksSummary;
  net: IGreeksSummary;
}

interface IOptionsGreeksSummaryTableProps {
  aevoPositions: IAevoPosition[];
  deribitPositions: IDeribitPosition[];
  deribitAccountData?: {
    BTC?: IDeribitAccountSummary;
    ETH?: IDeribitAccountSummary;
    SOL?: IDeribitAccountSummary;
  };
}

const formatGreek = (value: number): string => currency(value, { precision: 4 }).format({ symbol: "" });

const getValueColor = (value: number): string => {
  if (value > 0) return COLORS.green.one;
  if (value < 0) return COLORS.red.one;
  return TEXT_COLORS.one;
};

export function OptionsGreeksSummaryTable({
  aevoPositions,
  deribitPositions,
  deribitAccountData,
}: IOptionsGreeksSummaryTableProps) {
  const greeksByAsset = useMemo(() => {
    const summary: { [asset: string]: IAssetGreeksSummary } = {};

    // Initialize for common assets
    ["ETH", "BTC", "SOL"].forEach((asset) => {
      summary[asset] = {
        aevo: { delta: 0, gamma: 0, vega: 0, theta: 0 },
        deribit: { delta: 0, gamma: 0, vega: 0, theta: 0 },
        aevoPerp: { delta: 0 },
        deribitPerp: { delta: 0 },
        deribitBalance: { delta: 0 },
        net: { delta: 0, gamma: 0, vega: 0, theta: 0 },
      };
    });

    // First, create a map of Deribit per-contract Greeks by instrument
    const deribitGreeksMap: { [key: string]: { delta: number; gamma: number; vega: number; theta: number } } = {};
    deribitPositions.forEach((pos) => {
      if (pos.kind !== "option") return;
      const normalizedName = normalizeInstrumentName(pos.instrument_name || "");
      const size = Number(pos.size || 0);

      // Deribit Greeks are for the TOTAL position
      // Divide by SIGNED size to get per-contract Greeks with correct sign for option type
      // (calls will be positive delta, puts will be negative delta)
      if (size !== 0) {
        deribitGreeksMap[normalizedName] = {
          delta: Number(pos.delta || 0) / size,
          gamma: Math.abs(Number(pos.gamma || 0)) / Math.abs(size), // Gamma per-contract is always positive
          vega: Math.abs(Number(pos.vega || 0)) / Math.abs(size), // Vega per-contract is always positive
          theta: Number(pos.theta || 0) / size, // Theta can be positive or negative
        };
      }
    });

    // Process Aevo positions
    aevoPositions.forEach((pos) => {
      // Only process options positions
      if (pos.instrument_type !== "OPTION" || !pos.option) return;

      const asset = pos.asset || retrieveAssetByInstrumentName(pos.instrument_name);
      if (!summary[asset]) {
        summary[asset] = {
          aevo: { delta: 0, gamma: 0, vega: 0, theta: 0 },
          deribit: { delta: 0, gamma: 0, vega: 0, theta: 0 },
          aevoPerp: { delta: 0 },
          deribitPerp: { delta: 0 },
          deribitBalance: { delta: 0 },
          net: { delta: 0, gamma: 0, vega: 0, theta: 0 },
        };
      }

      const amount = Number(pos.amount);
      let side = 0;
      if (pos.side === "buy") {
        side = 1;
      } else if (pos.side === "sell") {
        side = -1;
      }
      const contracts = amount * side;
      const normalizedName = normalizeInstrumentName(pos.instrument_name);
      const deribitGreeks = deribitGreeksMap[normalizedName];

      if (deribitGreeks) {
        // Use Deribit per-contract Greeks * Aevo contracts
        summary[asset].aevo.delta += deribitGreeks.delta * contracts;
        summary[asset].aevo.gamma += deribitGreeks.gamma * contracts;
        summary[asset].aevo.vega += deribitGreeks.vega * contracts;
        summary[asset].aevo.theta += deribitGreeks.theta * contracts;
      } else {
        // Fallback: Aevo Greeks are already total, just apply sign
        summary[asset].aevo.delta += Number(pos.option.delta || 0) * side;
        summary[asset].aevo.gamma += Number(pos.option.gamma || 0) * side;
        summary[asset].aevo.vega += Number(pos.option.vega || 0) * side;
        summary[asset].aevo.theta += Number(pos.option.theta || 0) * side;
      }
    });

    // Process Deribit positions
    deribitPositions.forEach((pos) => {
      // Only process options positions
      if (pos.kind !== "option") return;

      const asset = retrieveAssetByInstrumentName(pos.instrument_name || "");
      if (!summary[asset]) {
        summary[asset] = {
          aevo: { delta: 0, gamma: 0, vega: 0, theta: 0 },
          deribit: { delta: 0, gamma: 0, vega: 0, theta: 0 },
          aevoPerp: { delta: 0 },
          deribitPerp: { delta: 0 },
          deribitBalance: { delta: 0 },
          net: { delta: 0, gamma: 0, vega: 0, theta: 0 },
        };
      }

      // Deribit Greeks are already the TOTAL for the position with correct signs
      // (short puts will have positive delta, short options will have positive theta, etc.)
      summary[asset].deribit.delta += Number(pos.delta || 0);
      summary[asset].deribit.gamma += Number(pos.gamma || 0);
      summary[asset].deribit.vega += Number(pos.vega || 0);
      summary[asset].deribit.theta += Number(pos.theta || 0);
    });

    // Process Aevo perp positions
    aevoPositions.forEach((pos) => {
      // Only process perp positions
      if (pos.instrument_type !== "PERPETUAL") return;

      const asset = pos.asset || retrieveAssetByInstrumentName(pos.instrument_name);
      if (!summary[asset]) {
        summary[asset] = {
          aevo: { delta: 0, gamma: 0, vega: 0, theta: 0 },
          deribit: { delta: 0, gamma: 0, vega: 0, theta: 0 },
          aevoPerp: { delta: 0 },
          deribitPerp: { delta: 0 },
          deribitBalance: { delta: 0 },
          net: { delta: 0, gamma: 0, vega: 0, theta: 0 },
        };
      }

      const amount = Number(pos.amount);
      let side = 0;
      if (pos.side === "buy") {
        side = 1;
      } else if (pos.side === "sell") {
        side = -1;
      }
      // Perp delta is side * amount
      summary[asset].aevoPerp.delta += side * amount;
    });

    // Process Deribit perp positions
    deribitPositions.forEach((pos) => {
      // Only process perp positions (futures with PERPETUAL in name)
      if (pos.kind !== "future" || !pos.instrument_name?.includes("PERPETUAL")) return;

      const asset = retrieveAssetByInstrumentName(pos.instrument_name || "");
      if (!summary[asset]) {
        summary[asset] = {
          aevo: { delta: 0, gamma: 0, vega: 0, theta: 0 },
          deribit: { delta: 0, gamma: 0, vega: 0, theta: 0 },
          aevoPerp: { delta: 0 },
          deribitPerp: { delta: 0 },
          deribitBalance: { delta: 0 },
          net: { delta: 0, gamma: 0, vega: 0, theta: 0 },
        };
      }

      // Deribit perp delta is already in the delta field
      summary[asset].deribitPerp.delta += Number(pos.delta || 0);
    });

    // Process Deribit balance
    if (deribitAccountData) {
      Object.keys(deribitAccountData).forEach((asset) => {
        const accountData = deribitAccountData[asset as keyof typeof deribitAccountData];
        if (accountData && accountData.balance !== undefined) {
          if (!summary[asset]) {
            summary[asset] = {
              aevo: { delta: 0, gamma: 0, vega: 0, theta: 0 },
              deribit: { delta: 0, gamma: 0, vega: 0, theta: 0 },
              aevoPerp: { delta: 0 },
              deribitPerp: { delta: 0 },
              deribitBalance: { delta: 0 },
              net: { delta: 0, gamma: 0, vega: 0, theta: 0 },
            };
          }
          // Balance delta equals the balance amount (since balance in BTC = BTC delta)
          summary[asset].deribitBalance.delta = Number(accountData.balance || 0);
        }
      });
    }

    // Calculate net values
    Object.keys(summary).forEach((asset) => {
      summary[asset].net.delta = summary[asset].aevo.delta + summary[asset].deribit.delta + summary[asset].aevoPerp.delta + summary[asset].deribitPerp.delta + summary[asset].deribitBalance.delta;
      summary[asset].net.gamma = summary[asset].aevo.gamma + summary[asset].deribit.gamma;
      summary[asset].net.vega = summary[asset].aevo.vega + summary[asset].deribit.vega;
      summary[asset].net.theta = summary[asset].aevo.theta + summary[asset].deribit.theta;
    });

    return summary;
  }, [aevoPositions, deribitPositions, deribitAccountData]);

  // Filter out assets with no positions
  const assetsWithPositions = useMemo(() => Object.entries(greeksByAsset).filter(([, summary]) => {
      const hasAevo =
        summary.aevo.delta !== 0 ||
        summary.aevo.gamma !== 0 ||
        summary.aevo.vega !== 0 ||
        summary.aevo.theta !== 0;
      const hasDeribit =
        summary.deribit.delta !== 0 ||
        summary.deribit.gamma !== 0 ||
        summary.deribit.vega !== 0 ||
        summary.deribit.theta !== 0;
      const hasAevoPerp = summary.aevoPerp.delta !== 0;
      const hasDeribitPerp = summary.deribitPerp.delta !== 0;
      const hasDeribitBalance = summary.deribitBalance.delta !== 0;
      return hasAevo || hasDeribit || hasAevoPerp || hasDeribitPerp || hasDeribitBalance;
    }), [greeksByAsset]);

  if (assetsWithPositions.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        padding: SPACING.three,
        backgroundColor: "#1a1a1a",
        borderRadius: "8px",
        marginBottom: SPACING.two,
      }}
    >
      <h3 style={{ marginBottom: SPACING.two }}>Options Greeks Summary</h3>
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "14px",
          }}
        >
          <thead>
            <tr style={{ borderBottom: "1px solid #333" }}>
              <th
                style={{
                  textAlign: "left",
                  padding: "8px",
                  color: TEXT_COLORS.two,
                  fontWeight: 600,
                }}
              >
                Asset
              </th>
              <th
                style={{
                  textAlign: "left",
                  padding: "8px",
                  color: TEXT_COLORS.two,
                  fontWeight: 600,
                }}
              >
                Exchange
              </th>
              <th
                style={{
                  textAlign: "right",
                  padding: "8px",
                  color: TEXT_COLORS.two,
                  fontWeight: 600,
                }}
              >
                Delta
              </th>
              <th
                style={{
                  textAlign: "right",
                  padding: "8px",
                  color: TEXT_COLORS.two,
                  fontWeight: 600,
                }}
              >
                Gamma
              </th>
              <th
                style={{
                  textAlign: "right",
                  padding: "8px",
                  color: TEXT_COLORS.two,
                  fontWeight: 600,
                }}
              >
                Vega
              </th>
              <th
                style={{
                  textAlign: "right",
                  padding: "8px",
                  color: TEXT_COLORS.two,
                  fontWeight: 600,
                }}
              >
                Theta
              </th>
            </tr>
          </thead>
          <tbody>
            {assetsWithPositions.map(([asset, summary]) => (
              <>
                {/* Aevo Row */}
                <tr key={`${asset}-aevo`} style={{ borderBottom: "1px solid #2a2a2a" }}>
                  <td
                    style={{
                      padding: "8px",
                      fontWeight: 600,
                      color: TEXT_COLORS.one,
                    }}
                    rowSpan={6}
                  >
                    {asset}
                  </td>
                  <td style={{ padding: "8px", color: TEXT_COLORS.two }}>Aevo</td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: getValueColor(summary.aevo.delta),
                    }}
                  >
                    {formatGreek(summary.aevo.delta)}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: getValueColor(summary.aevo.gamma),
                    }}
                  >
                    {formatGreek(summary.aevo.gamma)}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: getValueColor(summary.aevo.vega),
                    }}
                  >
                    {formatGreek(summary.aevo.vega)}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: getValueColor(summary.aevo.theta),
                    }}
                  >
                    {formatGreek(summary.aevo.theta)}
                  </td>
                </tr>

                {/* Deribit Row */}
                <tr key={`${asset}-deribit`} style={{ borderBottom: "1px solid #2a2a2a" }}>
                  <td style={{ padding: "8px", color: TEXT_COLORS.two }}>Deribit</td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: getValueColor(summary.deribit.delta),
                    }}
                  >
                    {formatGreek(summary.deribit.delta)}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: getValueColor(summary.deribit.gamma),
                    }}
                  >
                    {formatGreek(summary.deribit.gamma)}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: getValueColor(summary.deribit.vega),
                    }}
                  >
                    {formatGreek(summary.deribit.vega)}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: getValueColor(summary.deribit.theta),
                    }}
                  >
                    {formatGreek(summary.deribit.theta)}
                  </td>
                </tr>

                {/* Aevo Perp Row */}
                <tr key={`${asset}-aevo-perp`} style={{ borderBottom: "1px solid #2a2a2a" }}>
                  <td style={{ padding: "8px", color: TEXT_COLORS.two }}>Aevo Perp</td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: getValueColor(summary.aevoPerp.delta),
                    }}
                  >
                    {formatGreek(summary.aevoPerp.delta)}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: TEXT_COLORS.two,
                    }}
                  >
                    -
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: TEXT_COLORS.two,
                    }}
                  >
                    -
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: TEXT_COLORS.two,
                    }}
                  >
                    -
                  </td>
                </tr>

                {/* Deribit Perp Row */}
                <tr key={`${asset}-deribit-perp`} style={{ borderBottom: "1px solid #2a2a2a" }}>
                  <td style={{ padding: "8px", color: TEXT_COLORS.two }}>Deribit Perp</td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: getValueColor(summary.deribitPerp.delta),
                    }}
                  >
                    {formatGreek(summary.deribitPerp.delta)}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: TEXT_COLORS.two,
                    }}
                  >
                    -
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: TEXT_COLORS.two,
                    }}
                  >
                    -
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: TEXT_COLORS.two,
                    }}
                  >
                    -
                  </td>
                </tr>

                {/* Deribit Balance Row */}
                <tr key={`${asset}-deribit-balance`} style={{ borderBottom: "1px solid #2a2a2a" }}>
                  <td style={{ padding: "8px", color: TEXT_COLORS.two }}>Deribit Balance</td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: getValueColor(summary.deribitBalance.delta),
                    }}
                  >
                    {formatGreek(summary.deribitBalance.delta)}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: TEXT_COLORS.two,
                    }}
                  >
                    -
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: TEXT_COLORS.two,
                    }}
                  >
                    -
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: TEXT_COLORS.two,
                    }}
                  >
                    -
                  </td>
                </tr>

                {/* Net Row */}
                <tr
                  key={`${asset}-net`}
                  style={{
                    borderBottom: "2px solid #444",
                    backgroundColor: "#0f0f0f",
                  }}
                >
                  <td
                    style={{
                      padding: "8px",
                      color: TEXT_COLORS.one,
                      fontWeight: 600,
                    }}
                  >
                    Net
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: getValueColor(summary.net.delta),
                      fontWeight: 600,
                    }}
                  >
                    {formatGreek(summary.net.delta)}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: getValueColor(summary.net.gamma),
                      fontWeight: 600,
                    }}
                  >
                    {formatGreek(summary.net.gamma)}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: getValueColor(summary.net.vega),
                      fontWeight: 600,
                    }}
                  >
                    {formatGreek(summary.net.vega)}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      color: getValueColor(summary.net.theta),
                      fontWeight: 600,
                    }}
                  >
                    {formatGreek(summary.net.theta)}
                  </td>
                </tr>
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
